import { apiErrorMessageKey, isApiError } from '@shigo-ui/api-client';

import { dashboardMessages, dashboardT, type DashboardMessageKey } from './messages';

export function dashboardApiErrorMessage(error: unknown): string {
  if (!isApiError(error)) {
    return dashboardT('api.common.requestFailed');
  }

  const backendMessage = readBackendErrorMessage(error.body);
  if (backendMessage) {
    return backendMessage;
  }

  return (
    dashboardMessageFromKey(apiErrorMessageKey(error)) ?? dashboardT('api.common.requestFailed')
  );
}

export function dashboardErrorMessage(
  error: unknown,
  fallbackKey: DashboardMessageKey = 'api.common.requestFailed',
): string {
  if (isApiError(error)) {
    return dashboardApiErrorMessage(error);
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return dashboardT(fallbackKey);
}

function readBackendErrorMessage(body: unknown): string | undefined {
  const directMessage = readNonEmptyString(body);
  if (directMessage) {
    return directMessage;
  }

  const record = readRecord(body);
  const error = readRecord(record?.['error']);
  const messages = readStringArray(error?.['messages']) ?? readStringArray(record?.['messages']);
  const firstMessage = messages
    ?.map((message) => message.trim())
    .find((message) => message.length > 0);

  return (
    firstMessage ||
    readNonEmptyString(error?.['message']) ||
    readNonEmptyString(record?.['message'])
  );
}

function readRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function readStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const strings = value.filter((item): item is string => typeof item === 'string');
  return strings.length ? strings : undefined;
}

function readNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function dashboardMessageFromKey(key: string): string | undefined {
  if (!Object.prototype.hasOwnProperty.call(dashboardMessages, key)) {
    return undefined;
  }

  return dashboardT(key as DashboardMessageKey);
}
